/**
 * Revisions: a frozen copy of the drawing at an index (A, B, C…), shown in the title block,
 * listed in the revision table, and compared with the current drawing (visual diff).
 */
import { newId } from './ids.ts';
import type { Op } from './commands.ts';
import type { Element, Id, Project, ProjectContent, Revision, Wire } from './types.ts';

/** A, B, …, Z, AA, AB… */
export function revisionIndex(n: number): string {
  let s = '';
  let k = n;
  do {
    s = String.fromCharCode(65 + (k % 26)) + s;
    k = Math.floor(k / 26) - 1;
  } while (k >= 0);
  return s;
}

export function revisionsInOrder(project: Pick<Project, 'revisions'>): Revision[] {
  return Object.values(project.revisions ?? {}).sort(
    (a, b) => a.index.length - b.index.length || a.index.localeCompare(b.index),
  );
}

export const latestRevision = (project: Pick<Project, 'revisions'>): Revision | undefined =>
  revisionsInOrder(project).at(-1);

export function contentOf(project: Project): ProjectContent {
  const { revisions: _r, ...content } = project;
  return content;
}

/** Op adding a revision that freezes the project as it is now. */
export function createRevisionOp(
  project: Project,
  info: { description: string; author?: string; date?: string },
): Op {
  return {
    op: 'add',
    coll: 'revisions',
    value: {
      id: newId(),
      index: revisionIndex(Object.keys(project.revisions).length),
      date: info.date ?? new Date().toISOString().slice(0, 10),
      author: info.author ?? project.meta.author,
      description: info.description,
      snapshot: contentOf(project),
    },
  };
}

export interface ProjectDiff {
  added: Set<Id>;
  changed: Set<Id>;
  removedElements: Element[];
  removedWires: Wire[];
  /** The project the removed items belong to (to draw them). */
  before: ProjectContent;
  counts: { added: number; changed: number; removed: number };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What changed between a revision and now. A protective device is "changed" when its circuit
 * record changed (description, cable…), so the change shows on the drawing.
 */
export function diffProjects(before: ProjectContent, after: ProjectContent): ProjectDiff {
  const added = new Set<Id>();
  const changed = new Set<Id>();
  const removedElements: Element[] = [];
  const removedWires: Wire[] = [];
  for (const [coll, removed] of [
    ['elements', removedElements],
    ['wires', removedWires],
  ] as const) {
    const a = before[coll] as Record<Id, Element | Wire>;
    const b = after[coll] as Record<Id, Element | Wire>;
    for (const [id, v] of Object.entries(b)) {
      const old = a[id];
      if (!old) added.add(id);
      else if (!same(old, v)) changed.add(id);
    }
    for (const [id, v] of Object.entries(a)) if (!b[id]) (removed as Array<Element | Wire>).push(v);
  }
  const changedCircuits = new Set(
    Object.values(after.circuits)
      .filter((c) => before.circuits[c.id] && !same(before.circuits[c.id], c))
      .map((c) => c.id),
  );
  for (const e of Object.values(after.elements))
    if (e.circuitId && changedCircuits.has(e.circuitId) && !added.has(e.id)) changed.add(e.id);
  return {
    added,
    changed,
    removedElements,
    removedWires,
    before,
    counts: {
      added: added.size,
      changed: changed.size,
      removed: removedElements.length + removedWires.length,
    },
  };
}

/** Ops replacing the drawing with a revision's state (one undo step brings it back). */
export function restoreRevisionOps(project: Project, rev: Revision): Op[] {
  const ops: Op[] = [];
  const s = rev.snapshot;
  for (const id of Object.keys(project.wires)) ops.push({ op: 'remove', coll: 'wires', id });
  for (const id of Object.keys(project.elements)) ops.push({ op: 'remove', coll: 'elements', id });
  for (const id of Object.keys(project.circuits)) ops.push({ op: 'remove', coll: 'circuits', id });
  for (const id of Object.keys(project.symbols)) ops.push({ op: 'remove', coll: 'symbols', id });
  for (const id of Object.keys(project.sheets)) ops.push({ op: 'remove', coll: 'sheets', id });
  for (const v of Object.values(s.sheets)) ops.push({ op: 'add', coll: 'sheets', value: v });
  for (const v of Object.values(s.symbols ?? {})) ops.push({ op: 'add', coll: 'symbols', value: v });
  for (const v of Object.values(s.circuits)) ops.push({ op: 'add', coll: 'circuits', value: v });
  for (const v of Object.values(s.elements)) ops.push({ op: 'add', coll: 'elements', value: v });
  for (const v of Object.values(s.wires)) ops.push({ op: 'add', coll: 'wires', value: v });
  ops.push({ op: 'meta', patch: { ...s.meta } });
  return ops;
}
